import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Gauge, Loader2, Save, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminCard, AdminPageHeader } from "@/components/admin/admin-shell";
import { azError } from "@/lib/az-errors";
import { SEO_LABELS, fetchSeoSettings, saveSeoSetting } from "@/lib/seo-settings";

export const Route = createFileRoute("/_authenticated/admin/seo")({
  component: Page,
});

const GROUPS: { value: string; label: string; hint: string; keys: string[] }[] = [
  {
    value: "home",
    label: "Ana səhifə",
    hint: "Ana səhifənin başlığı, təsviri və açar sözləri",
    keys: ["home_title", "home_description", "home_keywords"],
  },
  {
    value: "category",
    label: "Kateqoriya SEO",
    hint: "Şablonda {kateqoriya} istifadə edin",
    keys: ["category_title_template", "category_description_template", "category_keywords_template"],
  },
  {
    value: "listing",
    label: "Elan SEO",
    hint: "Şablonda {basliq}, {qiymet}, {seher}, {kateqoriya} istifadə edin",
    keys: ["listing_title_template", "listing_description_template", "listing_keywords_template"],
  },
  {
    value: "keywords",
    label: "Açar sözlər",
    hint: "Bütün səhifələrə əlavə olunan ümumi açar sözlər",
    keys: ["global_keywords"],
  },
];

function Page() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["admin", "seo_settings"], queryFn: fetchSeoSettings });
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (query.data) setValues(query.data);
  }, [query.data]);

  const save = useMutation({
    mutationFn: async (keys: string[]) => {
      for (const key of keys) await saveSeoSetting(key, values[key] ?? "");
    },
    onSuccess: () => {
      toast.success("SEO ayarları yadda saxlanıldı");
      qc.invalidateQueries({ queryKey: ["admin", "seo_settings"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  return (
    <div>
      <AdminPageHeader
        title="SEO idarəetməsi"
        description="Ana səhifə, kateqoriya və elan meta məlumatları, açar sözlər"
      />

      {query.isLoading ? (
        <Loader2 className="mx-auto size-5 animate-spin text-primary" />
      ) : (
        <Tabs defaultValue="home">
          <TabsList className="flex-wrap rounded-full">
            {GROUPS.map((g) => (
              <TabsTrigger key={g.value} value={g.value} className="rounded-full">
                {g.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {GROUPS.map((g) => (
            <TabsContent key={g.value} value={g.value} className="mt-6">
              <AdminCard className="p-5">
                <p className="mb-4 text-xs text-muted-foreground">{g.hint}</p>
                <div className="grid gap-4">
                  {g.keys.map((key) => (
                    <div key={key}>
                      <Label className="mb-1.5 block text-xs font-semibold">
                        {SEO_LABELS[key] ?? key}
                      </Label>
                      {key.includes("description") || key.includes("keywords") ? (
                        <Textarea
                          className="min-h-24 rounded-2xl"
                          value={values[key] ?? ""}
                          onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                        />
                      ) : (
                        <Input
                          className="h-11 rounded-full"
                          value={values[key] ?? ""}
                          onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                        />
                      )}
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {(values[key] ?? "").length} simvol
                      </p>
                    </div>
                  ))}
                </div>
                <Button
                  className="mt-5 rounded-full"
                  disabled={save.isPending}
                  onClick={() => save.mutate(g.keys)}
                >
                  <Save className="size-4" /> Yadda saxla
                </Button>
              </AdminCard>
            </TabsContent>
          ))}
        </Tabs>
      )}

      <AdminCard className="mt-6 p-5 text-sm text-muted-foreground">
        <p className="mb-2 flex items-center gap-2 font-semibold text-foreground">
          <Search className="size-4" /> Avtomatik SEO
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Hər elan üçün SEO Title, Description və SEO URL avtomatik yaradılır</li>
          <li>
            <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="text-primary underline">
              sitemap.xml
            </a>{" "}
            və{" "}
            <a href="/robots.txt" target="_blank" rel="noreferrer" className="text-primary underline">
              robots.txt
            </a>{" "}
            aktivdir
          </li>
          <li>Canonical, Open Graph, Schema.org (JSON-LD) və şəkil alt mətnləri avtomatikdir</li>
          <li>Google indeksləşməsi: aktiv elanlar sitemap-a düşür və index, follow ilə açıqdır</li>
        </ul>
      </AdminCard>

      <AdminCard className="mt-6 p-5">
        <p className="mb-3 flex items-center gap-2 font-semibold text-foreground">
          <Gauge className="size-4" /> Performans və SEO vəziyyəti
        </p>
        <ul className="grid gap-2.5 text-sm">
          {PERF_ITEMS.map((item) => (
            <li key={item.label} className="flex items-start gap-2.5">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
              <div>
                <span className="font-medium text-foreground">{item.label}</span>
                <span className="block text-xs text-muted-foreground">{item.desc}</span>
              </div>
            </li>
          ))}
        </ul>
      </AdminCard>
    </div>
  );
}

const PERF_ITEMS = [
  {
    label: "Şəkillər WebP formatında, lazy loading aktivdir",
    desc: "İlk 2 şəkil eager/yüksək prioritet, qalanları lazy + async dekodla yüklənir",
  },
  {
    label: "Render-blocking optimallaşdırılıb",
    desc: "Backend bağlantısı preconnect/dns-prefetch ilə əvvəlcədən qurulur, ağır bloklar lazy-import edilir",
  },
  {
    label: "Keşləmə qaydaları qurulub",
    desc: "Statik fayllar 1 illik immutable keş, HTML NetworkFirst strategiyası ilə",
  },
  {
    label: "Meta title/description və heading strukturu",
    desc: "Hər səhifədə unikal title, description və tək H1 başlığı var",
  },
  {
    label: "Canonical URL və Open Graph",
    desc: "Bütün səhifələrdə self-canonical, og:url, og:image və twitter:card qurulub",
  },
  {
    label: "Schema.org strukturlaşdırılmış məlumatlar",
    desc: "Elan səhifələrində Product/Offer JSON-LD, saytda Organization sxemi",
  },
  {
    label: "sitemap.xml və robots.txt",
    desc: "Aktiv elanlar və kateqoriyalar avtomatik sitemap-a düşür, robots.txt indeksləməyə açıqdır",
  },
  {
    label: "SSR — axtarış sistemləri tam məzmunu görür",
    desc: "Server-side rendering sayəsində Google səhifələri JavaScript-siz oxuyur",
  },
];
